import { useTranslation } from 'react-i18next'
import { X, Package } from 'lucide-react'
import { useToteStore } from '@/stores/tote'
import { LocaleLink } from '@/components/LocaleLink'
import { MerchantHoverCard } from '@/components/offers/MerchantHoverCard'
import { buildOfferImageSrcSet } from '@/lib/offer-image-srcset'
import type { ToteItem } from '@/stores/tote'

export function ToteItemRow({ item }: { item: ToteItem }) {
  const { t } = useTranslation('common')
  const removeItem = useToteStore((s) => s.removeItem)
  const closeTote = useToteStore((s) => s.closeTote)

  const titleEl =
    item.streamSlug ? (
      <LocaleLink
        to={`/streams/${item.streamSlug}/offers/${item.offerId}`}
        onClick={closeTote}
        className="text-sm font-medium text-slate-800 dark:text-sand-100 hover:text-ocean-600 dark:hover:text-ocean-400 hover:underline transition-colors leading-snug block"
      >
        {item.title}
      </LocaleLink>
    ) : (
      <p className="text-sm font-medium text-slate-800 dark:text-sand-100 leading-snug">{item.title}</p>
    )

  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      {/* Thumbnail or placeholder */}
      <div className="h-12 w-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 dark:bg-slate-800 flex items-center justify-center mt-0.5">
        {item.imageUrl ? (
          <img
            src={item.images?.[0]?.sm ?? item.imageUrl}
            srcSet={item.images?.[0] ? buildOfferImageSrcSet(item.images[0]) : undefined}
            sizes="48px"
            alt={item.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <Package className="h-5 w-5 text-slate-400 dark:text-slate-500" strokeWidth={1.5} />
        )}
      </div>

      <div className="flex-1 min-w-0">
        {titleEl}
        <MerchantHoverCard merchantBusinessName={item.streamName} slug={item.streamSlug} avatarUrl={item.streamAvatarUrl} description={item.streamDescription} since={item.streamSince}>
          {item.streamSlug ? (
            <LocaleLink
              to={`/streams/${item.streamSlug}`}
              onClick={closeTote}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:underline transition-colors mt-0.5 block"
            >
              {item.streamName}
            </LocaleLink>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.streamName}</p>
          )}
        </MerchantHoverCard>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mt-0.5 tabular-nums">
          {item.drops.toLocaleString()} {t('common:tote.drops')}
        </p>
      </div>

      <button
        onClick={() => removeItem(item.offerId)}
        className="text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 -mr-1 mt-0.5 flex-shrink-0"
        aria-label={`Remove ${item.title} from tote`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
